import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ThemeToggle';
import { FileText, Sparkles, Target, Download, ChevronRight, ChevronLeft, Check, X, Users, Zap, ArrowRight, AlertTriangle, Shield, Eye, Clock, FileX, Bot, Briefcase, Rocket, Gift } from 'lucide-react';

const SLIDES = [
  {
    id: 'job-match',
    badge: 'AI JOB MATCHING',
    heading: 'Stop Applying Randomly. Apply Smarter With AI.',
    sub: 'Our AI analyzes your entire resume and finds the most relevant jobs and internships tailored to your skills, experience, and career goals.',
    cta: 'Unlock AI Job Matching — ₹299/Month',
    ctaIcon: 'rocket',
    ctaRoute: '/subscription',
    ctaSecondary: 'Try Job Search',
    ctaSecondaryRoute: '/job-search',
  },
  {
    id: 'builder',
    badge: 'AI RESUME BUILDER',
    heading: 'Land more interviews with AI-powered resumes',
    sub: 'ATS Check, AI Writer, and One-Click Job Tailoring make your resume stand out to recruiters.',
    cta: 'Build Your Resume',
    ctaSecondary: 'Get Your Resume Score',
  },
  {
    id: 'fresher',
    badge: 'ONE-PAGE RESUME',
    heading: 'Perfect single-page resume for freshers',
    sub: 'LaTeX-quality formatting, ATS-friendly structure, and AI-powered content — designed specifically for first-time job seekers.',
    cta: 'Create One-Page Resume',
    ctaSecondary: 'See Templates',
  },
  {
    id: 'optimizer',
    badge: 'ATS OPTIMIZER',
    heading: 'Boost your ATS score by 30%+ instantly',
    sub: 'Paste a job description and AI optimizes your resume with missing keywords, action verbs, and metrics.',
    cta: 'Optimize Now',
    ctaSecondary: 'Learn More',
  },
];

const REJECTION_REASONS = [
  { icon: FileX, title: 'No ATS Formatting', desc: 'Most resumes fail ATS parsing due to wrong headers, fonts, or table layouts.' },
  { icon: Eye, title: 'Weak First Impression', desc: 'Recruiters spend 6 seconds. If your summary doesn\'t hook them, you\'re out.' },
  { icon: AlertTriangle, title: 'Missing Keywords', desc: 'Job-specific keywords missing from your resume means automatic rejection.' },
  { icon: Clock, title: 'Too Long / Too Short', desc: 'Freshers with 3-page resumes or seniors with 1 page — both get filtered.' },
];

const SOLUTIONS = [
  { icon: Bot, title: 'AI Resume Writer', desc: 'Generates professional bullet points with action verbs and real metrics.', color: '#FB923C' },
  { icon: Target, title: 'ATS Score Checker', desc: 'Scores your resume against any job description and shows missing keywords.', color: '#38BDF8' },
  { icon: Zap, title: 'One-Click Optimization', desc: 'AI rewrites your resume to match the job — no keyword stuffing, no fake data.', color: '#F59E0B' },
  { icon: Shield, title: 'LaTeX PDF Export', desc: 'Pixel-perfect PDF with tight spacing, clean margins, and ATS-friendly formatting.', color: '#A78BFA' },
  { icon: FileText, title: 'One-Page Resume', desc: 'Specially designed for freshers — fit everything on one clean page.', color: '#F472B6' },
  { icon: Sparkles, title: 'Multiple Templates', desc: 'Modern, Classic, and Minimal — all tested with top applicant tracking systems.', color: '#34D399' },
];

function HeroSlide({ slide, navigate }) {
  const primaryRoute = slide.ctaRoute || '/register';
  const secondaryRoute = slide.ctaSecondaryRoute || '/register';
  const isJobMatch = slide.id === 'job-match';
  return (
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FB923C] mb-4 block">{slide.badge}</span>
        <h1 className="text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-none font-black text-slate-900 dark:text-white mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} data-testid="hero-heading">
          {slide.heading}
        </h1>
        <p className="text-lg md:text-xl leading-relaxed text-slate-600 dark:text-slate-400 mb-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{slide.sub}</p>
        <div className="flex flex-wrap gap-4">
          <Button data-testid="hero-cta-btn" onClick={() => navigate(primaryRoute)} className="bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-8 py-4 text-lg transition-all hover:-translate-y-1 shadow-[4px_4px_0px_#0F172A] hover:shadow-[6px_6px_0px_#0F172A]">
            {slide.ctaIcon === 'rocket' && <Rocket className="w-5 h-5 mr-2 inline" />}
            {slide.cta}
          </Button>
          <Button data-testid="hero-score-btn" onClick={() => navigate(secondaryRoute)} className="bg-white dark:bg-slate-800 border-2 border-slate-900 dark:border-slate-500 text-slate-900 dark:text-white font-bold rounded-none px-8 py-4 text-lg transition-all hover:bg-slate-50 shadow-[4px_4px_0px_#0F172A]">
            {slide.ctaSecondary}
          </Button>
        </div>
      </div>
      <div className="relative hidden lg:flex justify-center">
        {isJobMatch ? (
          <JobMatchVisual />
        ) : (
          <>
            <div className="relative transform rotate-2 hover:rotate-0 transition-transform duration-500">
              <div className="aspect-[210/297] w-72 bg-white border border-slate-200 shadow-[8px_8px_0px_#0F172A] p-8">
                <div className="space-y-4">
                  <div className="h-3 bg-slate-900 w-2/3" /><div className="h-2 bg-slate-300 w-1/2" /><div className="h-2 bg-slate-300 w-1/3" />
                  <div className="mt-6 space-y-2"><div className="h-2 bg-[#FB923C] w-full" /><div className="h-2 bg-slate-200 w-5/6" /><div className="h-2 bg-slate-200 w-4/5" /></div>
                  <div className="mt-6 space-y-2"><div className="h-2 bg-slate-900 w-1/3" /><div className="h-2 bg-slate-200 w-full" /><div className="h-2 bg-slate-200 w-5/6" /></div>
                </div>
              </div>
            </div>
            <div className="absolute -top-4 -right-4 bg-[#FB923C] rounded-full p-4 shadow-[4px_4px_0px_#0F172A]">
              <span className="text-2xl font-black text-slate-900">85%</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function JobMatchVisual() {
  const matches = [
    { title: 'Senior Data Scientist', co: 'Microsoft', score: 92, tier: 1 },
    { title: 'ML Engineer · LLM', co: 'Razorpay', score: 84, tier: 1 },
    { title: 'AI Research Engineer', co: 'Flipkart', score: 71, tier: 2 },
  ];
  return (
    <div className="relative w-80 transform -rotate-2 hover:rotate-0 transition-transform duration-500">
      <div className="bg-white dark:bg-slate-800 border-2 border-slate-900 dark:border-slate-500 shadow-[8px_8px_0px_#FB923C] p-5 space-y-3">
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-2 mb-2">
          <span className="text-[10px] tracking-widest font-black text-slate-900 dark:text-white uppercase">Best Matches</span>
          <span className="text-[10px] font-bold text-[#EA580C] bg-[#FFEDD5] dark:bg-orange-900/30 px-2 py-0.5">LIVE</span>
        </div>
        {matches.map((m) => (
          <div key={m.title} className={`border-l-4 ${m.tier === 1 ? 'border-emerald-500' : 'border-blue-500'} bg-slate-50 dark:bg-slate-900/40 p-3`}>
            <div className="flex items-center justify-between mb-1">
              <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 ${m.tier === 1 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'}`}>
                {m.score}% ATS
              </span>
              <span className="text-[9px] text-slate-400 uppercase">Tier {m.tier}</span>
            </div>
            <p className="text-sm font-bold text-slate-900 dark:text-white leading-tight">{m.title}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">{m.co}</p>
          </div>
        ))}
      </div>
      <div className="absolute -top-4 -left-4 bg-slate-900 text-white rounded-full px-4 py-2 shadow-[4px_4px_0px_#FB923C]">
        <span className="text-xs font-black tracking-wider">AI MATCHED</span>
      </div>
    </div>
  );
}

function RejectionSection() {
  return (
    <section className="py-20 md:py-28 bg-red-50 dark:bg-red-950/30" data-testid="rejection-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-14">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-red-500 mb-3 block">THE HARD TRUTH</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tighter font-black text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Why most resumes get rejected
          </h2>
          <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">75% of resumes are rejected before a human ever sees them. Here's why.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {REJECTION_REASONS.map((r) => (
            <div key={r.title} className="bg-white dark:bg-slate-800 border border-red-200 dark:border-red-900 p-6 hover:-translate-y-1 transition-all" data-testid="rejection-card">
              <div className="bg-red-100 dark:bg-red-900/50 p-2.5 inline-block mb-4">
                <r.icon className="w-6 h-6 text-red-600 dark:text-red-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{r.title}</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SolutionsSection({ navigate }) {
  return (
    <section className="py-20 md:py-28 bg-white dark:bg-slate-900" data-testid="solutions-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-14">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FB923C] mb-3 block">THE SOLUTION</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tighter font-black text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Aarova AI solves all these problems
          </h2>
          <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">Our AI-powered platform ensures your resume passes ATS, impresses recruiters, and lands you interviews.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SOLUTIONS.map((s) => (
            <div key={s.title} className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 hover:-translate-y-1 hover:shadow-[4px_4px_0px_#0F172A] dark:hover:shadow-[4px_4px_0px_#38bdf8] transition-all cursor-pointer" data-testid="solution-card">
              <div className="p-2.5 inline-block mb-4" style={{ backgroundColor: s.color + '20' }}>
                <s.icon className="w-6 h-6" style={{ color: s.color }} strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{s.title}</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Button data-testid="solutions-cta-btn" onClick={() => navigate('/register')} className="bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-10 py-4 text-lg transition-all hover:-translate-y-1 shadow-[4px_4px_0px_#0F172A]">
            Start Building for Free <ArrowRight className="w-5 h-5 ml-2 inline" />
          </Button>
        </div>
      </div>
    </section>
  );
}

function ATSSection({ navigate }) {
  return (
    <section className="relative py-20 md:py-28 overflow-hidden" data-testid="ats-section">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-purple-900" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-4xl md:text-5xl tracking-tighter leading-tight font-black text-white mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} data-testid="ats-heading">
              Resumes optimized for Applicant Tracking Systems
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-slate-300 mb-10" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              All templates are tested with top ATS software. Clean layouts, readable fonts, and standard sections — nothing gets lost.
            </p>
            <Button data-testid="ats-cta-btn" onClick={() => navigate('/register')} className="bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-8 py-4 text-lg transition-all hover:-translate-y-1 shadow-[4px_4px_0px_#0F172A]">
              Build an ATS-Friendly Resume
            </Button>
          </div>
          <div className="space-y-5">
            {[
              { icon: FileText, text: 'Readable contact information' },
              { icon: Target, text: 'Full experience section parsing' },
              { icon: Zap, text: 'Optimized skills section' },
            ].map((f) => (
              <div key={f.text} className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-5 flex items-center gap-4 hover:bg-white/15 transition-colors" data-testid="ats-feature-card">
                <div className="bg-white rounded-lg p-3 shrink-0">
                  <f.icon className="w-6 h-6 text-purple-600" strokeWidth={1.5} />
                </div>
                <span className="text-base font-semibold text-white">{f.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => setCurrentSlide((prev) => (prev + 1) % SLIDES.length), []);
  const prevSlide = useCallback(() => setCurrentSlide((prev) => (prev - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <FileText className="w-8 h-8 text-[#FB923C]" strokeWidth={2} />
              <span className="text-2xl font-black text-slate-900 dark:text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Aarova AI</span>
            </div>
            <div className="hidden md:flex items-center gap-6" data-testid="nav-labels">
              {['Find Jobs', 'Resume Builder', 'One-Page Resume', 'ATS Optimizer', 'Refer & Earn', 'Pricing'].map((label) => (
                <button
                  key={label}
                  data-testid={`nav-label-${label.toLowerCase().replace(/\s|&/g, '-').replace(/--+/g, '-')}`}
                  onClick={() => {
                    const routes = {
                      'Find Jobs': '/job-search',
                      'Pricing': '/subscription',
                      'One-Page Resume': '/one-page-builder',
                      'ATS Optimizer': '/ats-optimizer',
                      'Refer & Earn': '/refer-and-earn',
                    };
                    navigate(routes[label] || '/register');
                  }}
                  className={`text-sm font-medium transition-colors relative group ${label === 'Find Jobs' ? 'text-[#EA580C] dark:text-[#FB923C] font-bold' : label === 'Refer & Earn' ? 'text-emerald-600 dark:text-emerald-400 font-bold' : 'text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'}`}
                >
                  {label === 'Find Jobs' && <Briefcase className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}
                  {label === 'Refer & Earn' && <Gift className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}
                  {label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#FB923C] group-hover:w-full transition-all duration-300" />
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-3 items-center">
            <ThemeToggle />
            <Button data-testid="nav-login-btn" onClick={() => navigate('/login')} className="bg-white dark:bg-slate-800 border-2 border-slate-900 dark:border-slate-500 text-slate-900 dark:text-white font-bold rounded-none px-6 py-2 transition-all hover:bg-slate-50 shadow-[3px_3px_0px_#0F172A]">
              Sign In
            </Button>
            <Button data-testid="nav-signup-btn" onClick={() => navigate('/register')} className="bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-6 py-2 transition-all hover:-translate-y-0.5 shadow-[3px_3px_0px_#0F172A]">
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Slider */}
      <section className="relative py-20 md:py-28" data-testid="hero-slider">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <HeroSlide slide={SLIDES[currentSlide]} navigate={navigate} />
          <div className="flex items-center justify-center gap-4 mt-12">
            <button data-testid="slider-prev" onClick={prevSlide} className="p-2 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              <ChevronLeft className="w-5 h-5 text-slate-700 dark:text-slate-300" />
            </button>
            <div className="flex gap-2">
              {SLIDES.map((s, idx) => (
                <button key={s.id} data-testid={`slider-dot-${idx}`} onClick={() => setCurrentSlide(idx)} className={`h-2 rounded-full transition-all duration-300 ${idx === currentSlide ? 'w-8 bg-[#FB923C]' : 'w-2 bg-slate-300 dark:bg-slate-600 hover:bg-slate-400'}`} />
              ))}
            </div>
            <button data-testid="slider-next" onClick={nextSlide} className="p-2 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              <ChevronRight className="w-5 h-5 text-slate-700 dark:text-slate-300" />
            </button>
          </div>
        </div>
      </section>

      {/* Why Resumes Get Rejected */}
      <RejectionSection />

      {/* Aarova AI Solves All */}
      <SolutionsSection navigate={navigate} />

      {/* ATS Section */}
      <ATSSection navigate={navigate} />

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#FB923C]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl tracking-tighter leading-tight font-black text-slate-900 mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} data-testid="cta-heading">
            Start building your winning resume today
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-slate-900 mb-8">Join thousands of job seekers who landed their dream roles with Aarova AI</p>
          <Button data-testid="cta-get-started-btn" onClick={() => navigate('/register')} className="bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-none px-12 py-5 text-lg transition-all hover:-translate-y-1 shadow-[6px_6px_0px_#0F172A]">
            Get Started Free <ArrowRight className="w-5 h-5 ml-2 inline" />
          </Button>
        </div>
      </section>
    </div>
  );
}
